import * as React from 'react';
import * as Images from 'pos/Images';
import { ActionButton } from 'pos/ui';
import { Res } from 'core/api';
import * as Common from './ShowInvoicesScreen.Common';
import { Props } from './ShowInvoicesScreen.Common';
import { InvoiceFacade } from 'pos/facades';
import { HStack, Table, TableScrollArea, VStack, Text, SegmentGroup } from '@chakra-ui/react';
import { TableCellProps } from 'node_modules/@chakra-ui/react/dist/types/components/date-picker/namespace';
import { SvgIcon } from 'core/ui-next';
import * as UI from 'core/ui';
import { DateUtils } from 'core/utils';


export default function PhoneShowInvoices(props: Props) {

	//Props
	const { businessDay, cancel, close } = props;

	//State
	const [selection, setSelection] = React.useState<Nullable<Common.Selection>>(null);
	const [invoices, setInvoices] = React.useState<Common.InvoiceInListModel[]>([]);


	// Ref con la selección "viva" para que reload no dependa de `selection` y
	// no re-cree la callback en cada cambio (equivalente a leer this.state en la clase).
	const selectionRef = React.useRef(selection);
	React.useEffect(() => { selectionRef.current = selection; });


	// Recarga las facturas: todas las del día de negocio o solo las del POS actual,
	// según la preferencia guardada (equivalente al reload de la clase antigua).
	const reload = React.useCallback(async () => {
		const newInvoices = Common.mustShowAllInvoices()
			? await InvoiceFacade.getBetweenBusinessDays(businessDay, businessDay)
			: await InvoiceFacade.getByPosAndBusinessDay(businessDay);

		const result = await Common.updateInvoices(newInvoices, selectionRef.current);
		setInvoices(result.invoices);
		setSelection(result.selection);
	}, [businessDay]);

	// Suscribe eventos de dominio y dispara la carga inicial (equivalente al
	// antiguo ShowInvoices.onComponentDidMount).
	Common.useInvoiceReload(reload);


	// Selecciona una factura de la lista y carga su preview.
	const onSelectedInvoice = React.useCallback(async (invoiceId: Nullable<number>) => {
		setSelection(await Common.select(invoiceId));
	}, []);


	// Alterna entre "mostrar todas las facturas" y "solo las del POS", limpiando la selección y recargando.
	const onToggleShowAllInvoices = React.useCallback(async () => {
		Common.toggleShowAllInvoices();
		setSelection(null);
		selectionRef.current = null;
		await reload();
	}, [reload]);


	// Muestra el preview de impresión y, si se confirma, imprime la factura.
	const showPreviewToPrint = React.useCallback(async () => {
		const result = await Common.showPreview(Res.Web.PRINT_DOCUMENT(), Images.PrintTicket, selection);
		if (!result.ok)
			return;

		await Common.printInvoice(selection);
	}, [selection]);


	// Muestra el preview de reapertura y, si se confirma, reabre la factura y navega al ticket resultante.
	const showReopenPreview = React.useCallback(async () => {
		const result = await Common.showPreview(Res.Web.INVOICE_REOPEN(), Images.ReopenTicket, selection);
		if (result.ok) {
			const reopenResult = await Common.reopenInvoice(selection);
			if (reopenResult.ok)
				close(reopenResult.value);
		}
	}, [selection, close]);


	// Muestra el preview de abono y, si se confirma, crea el abono total y cierra la pantalla.
	const showRefundPreview = React.useCallback(async () => {
		const result = await Common.showPreview(Res.Web.INVOICE_REFUND(), Images.RefundTicket, selection);
		if (result.ok) {
			await Common.createTotalRefund(selection);
			cancel();
		}
	}, [selection, cancel]);


	// Cambia la propina de la factura seleccionada.
	const changeTip = React.useCallback(async () => {
		await Common.setTips(selection);
	}, [selection]);


	// Derivados para el render (equivalentes a los usados en getActionButtonsDefs/getContent de la clase).
	const isRefund = selection !== null && selection.invoice.IsRefund;
	const showAllInvoices = Common.mustShowAllInvoices();


	return <Common.ShowInvoices
		cancel={cancel}
		content={
			<VStack width={'full'} height={'full'} gap={'6'} paddingX={'2'} paddingY={'4'} overflow={'hidden'}>
				<HStack>
					<SegmentGroup.Root
						value={showAllInvoices ? 'all' : 'pos'}
						onValueChange={() => onToggleShowAllInvoices()}
						css={{
							'--segment-indicator-bg': 'var(--chakra-colors-primary)'
						}}
					>
						<SegmentGroup.Indicator />
						<SegmentGroup.Item value={'pos'}>
							<SegmentGroup.ItemText _checked={{ color: 'white' }}>{Res.Web.SHOW_INVOICES_CURRENT_TERMINAL_LBL()}</SegmentGroup.ItemText>
							<SegmentGroup.ItemHiddenInput />
						</SegmentGroup.Item>
						<SegmentGroup.Item value={'all'}>
							<SegmentGroup.ItemText _checked={{ color: 'white' }}>{Res.Web.SHOW_INVOICES_ALL_TERMINALS_LBL()}</SegmentGroup.ItemText>
							<SegmentGroup.ItemHiddenInput />
						</SegmentGroup.Item>
					</SegmentGroup.Root>
				</HStack>
				<HStack width={'full'} height={'full'} overflow={'auto'}>
					<TableScrollArea maxWidth={'stretch'} minWidth={'full'} minHeight={'full'} maxHeight={'stretch'} rounded={'md'}>
						<Table.Root
							size={'sm'}
							striped
							stickyHeader
							interactive
						>
							<Table.Header>
								<Table.Row>
									<Table.ColumnHeader onClick={() => { }} cursor={'pointer'}>
										<Text fontSize={'sm'} fontWeight={'semibold'} color={{ base: '{colors.neutral.800}', _dark: 'white' }}>{Res.Web.SHOW_INVOICES_NUMBER_LBL()}</Text>
									</Table.ColumnHeader>
									<Table.ColumnHeader onClick={() => { }} cursor={'pointer'} textAlign="end">
										<Text fontSize={'sm'} fontWeight={'semibold'} color={{ base: '{colors.neutral.800}', _dark: 'white' }}>{Res.Web.SHOW_INVOICES_NET_AMOUNT_CLM()}</Text>
									</Table.ColumnHeader>
								</Table.Row>
							</Table.Header>
							<Table.Body>
								{invoices.length === 0 && (
									<Table.Row>
										<Table.Cell colSpan={2} textAlign={'center'}>
											<Text fontSize={'sm'} color={{ base: 'neutral.500', _dark: 'neutral.400' }}>{Res.Web.SHOW_INVOICES_NO_INVOICES_LBL()}</Text>
										</Table.Cell>
									</Table.Row>
								)}
								{invoices.length > 0 && invoices.map((invoice) => {
									const cellBackGround: TableCellProps['bg'] = invoice.Id === selection?.invoice.Id ? { base: '{colors.blue.200}', _dark: '{colors.blue.500}' } : undefined;
									return (
										<Table.Row key={invoice.Id} onClick={() => onSelectedInvoice(invoice.Id)} cursor={'pointer'}>
											<Table.Cell background={cellBackGround}>
												<VStack alignItems={'flex-start'} gap={'3'}>
													<Text fontSize={'sm'} fontWeight={'semibold'}>{invoice.SerieAndNumber}</Text>
													<HStack gap={'1'}>
														<SvgIcon src={Images.CalendarClock} width={'4'} height={'4'} />
														<Text fontSize={'sm'}>{DateUtils.formatDate(invoice.CreationDate, 'dd/MM/yyyy HH:mm')}</Text>
													</HStack>
												</VStack>
											</Table.Cell>
											<Table.Cell background={cellBackGround}>
												<VStack alignItems={'flex-end'} gap={'3'}>                                                    
													<Text fontSize={'sm'} fontWeight={'semibold'}>{UI.Format.asMoney(invoice.NetAmount)}</Text>
													<HStack gap={'1'}>
														<SvgIcon src={Images.SelectSaleLocation} width={'4'} height={'4'} />
														<Text fontSize={'sm'}>{invoice.SaleCenter}/{invoice.SaleLocation}</Text>
													</HStack>
												</VStack>
											</Table.Cell>
										</Table.Row>);
								})}
							</Table.Body>
						</Table.Root>
					</TableScrollArea >
				</HStack>
			</VStack>
		}
		footer={
			<>
				{isRefund ? null : <ActionButton title={Res.Web.REOPEN_INVOICE()} image={Images.ReopenTicket} action={showReopenPreview} />}
				{isRefund ? null : <ActionButton title={Res.Web.INVOICE_REFUND()} image={Images.RefundTicket} action={showRefundPreview} />}
				{isRefund ? null : <ActionButton title={Res.Web.SHOW_INVOICES_CHANGE_TIP_BTN()} image={Images.TipSettings} action={changeTip} />}
				<ActionButton title={Res.Web.SHOW_INVOICES_PRINT_INVOICE_BTN()} image={Images.PrintTicket} action={showPreviewToPrint} />
			</>
		}
	/>;
}